import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  IPurchaseOrder,
  ISupplier,
  PurchaseOrderStatus,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

const STATUS_VARIANT: Record<
  PurchaseOrderStatus,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  draft: 'neutral',
  placed: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'danger',
};

const STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  placed: 'Placed',
  partially_received: 'Partial',
  received: 'Received',
  cancelled: 'Cancelled',
};

// Sub-phase U.2a — data-table list of purchase orders. Row click → edit
// page; per-row action column carries Place (Draft only) + Cancel
// (Draft / Placed) for owners. Status filter remains in the top bar.
@customElement('purchase-orders-list-page')
export class PurchaseOrdersListPage extends Base {
  @state() private _pos: IPurchaseOrder[] = [];
  @state() private _suppliersById = new Map<string, ISupplier>();
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _statusFilter = '';
  @state() private _busy = false;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      .filter-bar {
        display: flex;
        gap: 8px;
        align-items: center;
        flex: none;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .filter-bar sl-select {
        min-width: 180px;
      }
      .row-actions {
        display: flex;
        gap: 2px;
        justify-content: flex-end;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
      .money {
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;
    await this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [poR, supR] = await Promise.all([
      api.listPurchaseOrders(this.activeShop.shopName, {
        status: this._statusFilter || undefined,
      }),
      api.listSuppliers(this.activeShop.shopName, true),
    ]);
    if (!poR.success || !poR.data) {
      this._error = poR.error ?? 'Failed to load purchase orders.';
    } else {
      this._pos = poR.data;
    }
    if (supR.success && supR.data) {
      this._suppliersById = new Map(supR.data.map((s) => [s.id, s]));
    }
    this._loading = false;
  }

  private _openEdit(po: IPurchaseOrder) {
    router.navigate(
      resolveRouterPath(`purchase-orders/${encodeURIComponent(po.id)}`)
    );
  }

  private async _place(po: IPurchaseOrder, ev: Event) {
    ev.stopPropagation();
    if (!this.activeShop || this._busy) return;
    if (
      !confirm(
        `Place this PO with ${this._suppliersById.get(po.supplierId)?.name ?? po.supplierId}?\nOnce placed, lines can't be edited.`
      )
    )
      return;
    this._busy = true;
    const r = await ApiFactory.authenticated().placePurchaseOrder(
      this.activeShop.shopName,
      po.id
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Place failed.';
      return;
    }
    await this._load();
  }

  private async _cancel(po: IPurchaseOrder, ev: Event) {
    ev.stopPropagation();
    if (!this.activeShop || this._busy) return;
    const reason = prompt('Cancellation reason (optional):') ?? undefined;
    this._busy = true;
    const r = await ApiFactory.authenticated().cancelPurchaseOrder(
      this.activeShop.shopName,
      po.id,
      {
        reason: reason || undefined,
      }
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Cancel failed.';
      return;
    }
    await this._load();
  }

  private get _dataProvider() {
    return createArrayDataProvider<IPurchaseOrder>(() => this._pos, {
      filterFn: (item, text) => {
        const supplier = this._suppliersById.get(item.supplierId);
        return (
          (supplier?.name ?? '').toLowerCase().includes(text) ||
          item.supplierId.toLowerCase().includes(text) ||
          (item.referenceNumber ?? '').toLowerCase().includes(text)
        );
      },
    });
  }

  private _columns(isOwner: boolean): DataTableColumnDef[] {
    return [
      {
        title: 'Status',
        fieldName: 'status',
        sortable: true,
        width: '110px',
        displayValue: (_t, po: IPurchaseOrder) =>
          html`<sl-badge variant=${STATUS_VARIANT[po.status]} pill
            >${STATUS_LABEL[po.status]}</sl-badge
          >`,
      },
      {
        title: 'Ref #',
        fieldName: 'referenceNumber',
        sortable: true,
        width: '120px',
        displayValue: (_t, po: IPurchaseOrder) => po.referenceNumber || '—',
      },
      {
        title: 'Supplier',
        fieldName: 'supplierId',
        sortable: true,
        minWidth: '160px',
        displayValue: (_t, po: IPurchaseOrder) => {
          const s = this._suppliersById.get(po.supplierId);
          return s?.name ?? po.supplierId;
        },
      },
      {
        title: 'Created',
        fieldName: 'createdUtc',
        sortable: true,
        width: '160px',
        isDefaultSortDirectionAscending: false,
        displayValue: (_t, po: IPurchaseOrder) =>
          new Date(po.createdUtc).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      },
      {
        title: 'Lines',
        fieldName: 'lines',
        width: '70px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, po: IPurchaseOrder) => String(po.lines.length),
      },
      {
        title: 'Subtotal',
        fieldName: 'subtotal',
        sortable: true,
        width: '110px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, po: IPurchaseOrder) => po.subtotal.toFixed(2),
      },
      {
        title: '',
        fieldName: 'id',
        width: '108px',
        fixed: 'right',
        align: 'right',
        displayValue: (_t, po: IPurchaseOrder) => {
          if (!isOwner) return html``;
          const canPlace = po.status === 'draft';
          const canCancel = po.status === 'draft' || po.status === 'placed';
          if (!canPlace && !canCancel) return html``;
          return html`
            <div
              class="row-actions"
              @click=${(e: Event) => e.stopPropagation()}
            >
              ${
                canPlace
                  ? html`
                      <sl-icon-button
                        name="send-check"
                        label="Place"
                        @click=${(e: Event) => this._place(po, e)}
                      ></sl-icon-button>
                    `
                  : nothing
              }
              ${
                canCancel
                  ? html`
                      <sl-icon-button
                        name="x-circle"
                        label="Cancel"
                        @click=${(e: Event) => this._cancel(po, e)}
                      ></sl-icon-button>
                    `
                  : nothing
              }
            </div>
          `;
        },
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Purchase orders</h2>
        ${
          isOwner
            ? html`
                <sl-button
                  size="small"
                  variant="primary"
                  @click=${() => router.navigate(resolveRouterPath('purchase-orders/new'))}
                >
                  <sl-icon slot="prefix" name="plus"></sl-icon>
                  Draft new
                </sl-button>
              `
            : nothing
        }
      </header>

      <div class="filter-bar">
        <sl-select
          size="small"
          placeholder="All statuses"
          .value=${this._statusFilter}
          @sl-change=${(e: Event) => {
            this._statusFilter = (e.target as HTMLSelectElement).value;
            void this._load();
          }}
        >
          <sl-option value="">All</sl-option>
          <sl-option value="draft">Draft</sl-option>
          <sl-option value="placed">Placed</sl-option>
          <sl-option value="partially_received">Partially received</sl-option>
          <sl-option value="received">Received</sl-option>
          <sl-option value="cancelled">Cancelled</sl-option>
        </sl-select>
      </div>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._pos.length === 0
            ? html`<div class="empty">
                <p>No purchase orders match the filter.</p>
              </div>`
            : html`
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns(!!isOwner)}
                  .dataProvider=${this._dataProvider}
                  .keyevent=${(po: IPurchaseOrder) => po.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: IPurchaseOrder }>) =>
                    this._openEdit(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
